import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize axios with stored token on app start
const storedToken = localStorage.getItem('aeroops_token');
if (storedToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(storedToken);
    const [loading, setLoading] = useState(true);
    const [airline, setAirline] = useState(null);

    const clearAuth = useCallback(() => {
        localStorage.removeItem('aeroops_token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setAirline(null);
    }, []);

    const fetchUser = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
            // Fetch tenant/airline info
            if (response.data.tenant_id) {
                try {
                    const tenantRes = await axios.get(`${API}/tenant`);
                    setAirline(tenantRes.data.tenant);
                } catch (e) {
                    console.warn('Could not fetch tenant:', e);
                }
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            clearAuth();
        } finally {
            setLoading(false);
        }
    }, [clearAuth]);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token, fetchUser]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API}/auth/login`, { email, password });
            if (response.data.requires_password_change) {
                return { requires_password_change: true, tempToken: response.data.access_token, role: response.data.user?.role };
            }
            const { access_token, user: userData, tenant } = response.data;
            
            // Store token and update axios headers
            localStorage.setItem('aeroops_token', access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            
            // Update state
            setToken(access_token);
            setUser(userData);
            if (tenant) setAirline(tenant);
            
            return userData;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const logout = () => {
        clearAuth();
    };

    // Bloque C: primer ingreso con contraseña temporal
    const completeFirstChange = async (tempToken, currentPassword, newPassword) => {
        const res = await axios.post(`${API}/auth/password/first-change`, { current_password: currentPassword, new_password: newPassword },
            { headers: { Authorization: `Bearer ${tempToken}` } });
        const { access_token, user: userData, tenant } = res.data;
        localStorage.setItem('aeroops_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        if (tenant) setAirline(tenant);
        return userData;
    };

    const hasRole = (roles) => {
        if (!user) return false;
        if (typeof roles === 'string') return user.role === roles;
        return roles.includes(user.role);
    };

    // FASE 6: modulos vendibles (Tripulaciones, Revenue, AI, Predictive MX...).
    // Super admin (sin tenant) siempre true. Sin tenant cargado o enabled_modules null → todo habilitado (legacy).
    const hasModule = (moduleKey) => {
        if (!user) return false;
        if (user.role === 'superadmin' || !user.tenant_id) return true;
        if (!airline) return true;  // aun no cargo, no bloquear
        if (airline.enabled_modules === null || airline.enabled_modules === undefined) return true;
        return airline.enabled_modules.includes(moduleKey);
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            airline,
            loading,
            login,
            completeFirstChange,
            logout,
            hasRole,
            hasModule,
            refreshUser: fetchUser,
            isAuthenticated: !!user
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
